<template>
  <div class="dashboard-container">
    <div class="header">
      <div class="header-left">
        <div class="back-home-btn" @click="goBack">
          <span class="back-icon">←</span>
          <span class="back-text">返回主页</span>
        </div>
        <div class="project-date-group">
          <input
            type="week"
            v-model="selectedWeek"
            @change="onWeekChange"
            class="date-picker week-picker"
          />
        </div>
        <button class="export-btn" :disabled="exporting" @click="handleExport">
          {{ exporting ? '导出中...' : '导出 Excel' }}
        </button>
      </div>

      <div class="title">海康智慧电梯监测周排查</div>

      <div class="header-time">
        <span class="system-time">系统时间：{{ currentTime }}</span>
      </div>
    </div>

    <div class="page-content" :class="{ loading: loading }">
      <!-- 上周 / 本周历史触发 -->
      <div class="bottom-section">
        <div class="panel-card bottom-card events-panel">
          <div class="chart-title">上周历史触发（{{ lastWeekLabel }}）</div>
          <DailyEventAnalysis
            :chart-data="lastWeekChartData"
            :events="lastWeekEvents"
            :year-stats="lastWeekEventStats"
            :stat-labels="lastWeekStatLabels"
            enable-project-search
          />
        </div>

        <div class="panel-card bottom-card events-panel">
          <div class="chart-title">本周历史触发统计（{{ thisWeekLabel }}）</div>
          <DailyEventAnalysis
            :chart-data="thisWeekChartData"
            :events="thisWeekEvents"
            :year-stats="thisWeekEventStats"
            :stat-labels="thisWeekStatLabels"
            enable-project-search
          />
        </div>
      </div>

      <!-- （质量）安全风险隐患及评价 -->
      <div class="panel-card safety-input-panel">
        <div
          class="manual-note-block"
          v-for="field in safetyNoteFields"
          :key="field.key"
        >
          <div class="chart-title">{{ field.label }}</div>
          <textarea
            v-model="safetyInputs[field.key]"
            class="manual-textarea"
            :placeholder="`请输入${field.label.replace(/^\d+\./, '')}`"
          />
        </div>
        <div class="manual-note-block">
          <div class="chart-title">{{ safetyEvaluationTitle }}</div>
          <div class="evaluation-options">
            <div
              v-for="option in safetyEvaluationOptions"
              :key="option.value"
              class="evaluation-option"
              :class="{ active: safetyInputs.safetyEvaluation === option.value }"
              @click="safetyInputs.safetyEvaluation = option.value"
            >
              <span class="check-icon">
                {{ safetyInputs.safetyEvaluation === option.value ? '✓' : '☐' }}
              </span>
              <span class="option-label">{{ option.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="bottom-section work-order-section">
        <WorkOrderRecords
          :title="`上周工单未处理记录（${lastWeekLabel}）`"
          :records="lastWeekWorkOrderRecords"
          :show-project-address="true"
          hide-status
          :container-style="{ flex: '1', minHeight: '200px' }"
        />
        <WorkOrderRecords
          :title="`本周工单未处理记录（${thisWeekLabel}）`"
          :records="thisWeekWorkOrderRecords"
          :show-project-address="true"
          hide-status
          :container-style="{ flex: '1', minHeight: '200px' }"
        />
      </div>

      <!-- 签字确认 -->
      <div class="panel-card">
        <div class="chart-title">签字确认</div>
        <div class="signature-grid" :class="{ 'single-sign': staffSignList.length === 1 }">
          <div
            class="signature-block"
            v-for="(sign, index) in staffSignList"
            :key="'sign-' + index"
          >
            <span class="info-label">{{ sign.title }}</span>
            <div class="signature-box">
              <img
                v-if="sign.staffImage"
                :src="sign.staffImage"
                :alt="sign.title"
                class="signature-image"
                @error="onStaffSignImageError(index)"
              />
              <span v-else class="signature-placeholder">暂无签名</span>
            </div>
          </div>
        </div>
        <div v-if="!staffSignList.length" class="empty-tip">暂无签字信息</div>
      </div>
    </div>
  </div>
</template>

<script>
import WorkOrderRecords from '@/components/WorkOrderRecords.vue'
import DailyEventAnalysis from '@/components/DailyEventAnalysis.vue'
import { dashboardAPI } from '@/api/dashboard'
import { exportWeeklyInspectionReport } from '@/utils/exportExcel'
import {
  buildDailyEventChartData,
  buildDailyEventSummaryText,
  buildEventCountsFromEvents,
  buildEventStatsFromCounts,
  parseLastWeekEventStats,
  parseThisWeekEventStats,
  parseLastWeekEventStat,
  parseTodayEventStat
} from '@/config/dailyEventConfig'
import {
  dateToWeekInput,
  weekInputToDate,
  getWeekRange,
  getLastWeekRange,
  isDateInRange,
  formatDate
} from '@/utils/weekHelper'
import {
  WEEKLY_SAFETY_NOTE_FIELDS,
  WEEKLY_SAFETY_EVALUATION_TITLE,
  WEEKLY_SAFETY_EVALUATION_OPTIONS,
  createDefaultWeeklySafetyInputs
} from '@/config/weeklyInspectionConfig'

export default {
  name: 'WeeklyInspectionView',
  components: { WorkOrderRecords, DailyEventAnalysis },
  data() {
    return {
      loading: false,
      exporting: false,
      currentTime: '',
      timer: null,
      selectedWeek: dateToWeekInput(new Date()),
      selectedProjectId: '',
      communityList: [],
      staffSignList: [],
      lastWeekEvents: [],
      thisWeekEvents: [],
      lastWeekEventCounts: {},
      thisWeekEventCounts: {},
      lastWeekEventStats: {
        thisYear: 0,
        lastYear: 0,
        total: 0
      },
      thisWeekEventStats: {
        thisYear: 0,
        lastYear: 0,
        total: 0
      },
      oldEventStr: '',
      eventStr: '',
      lastWeekWorkOrderRecords: [],
      thisWeekWorkOrderRecords: [],
      safetyInputs: createDefaultWeeklySafetyInputs(),
      safetyNoteFields: WEEKLY_SAFETY_NOTE_FIELDS,
      safetyEvaluationTitle: WEEKLY_SAFETY_EVALUATION_TITLE,
      safetyEvaluationOptions: WEEKLY_SAFETY_EVALUATION_OPTIONS,
      lastWeekStatLabels: {
        first: '上周累计触发事件',
        second: '上上周累计触发事件',
        third: '总触发事件数'
      },
      thisWeekStatLabels: {
        first: '本周累计触发事件',
        second: '上周累计触发事件',
        third: '总触发事件数'
      }
    }
  },
  computed: {
    weekRange() {
      const anchor = weekInputToDate(this.selectedWeek)
      return getWeekRange(anchor)
    },
    lastWeekRange() {
      const anchor = weekInputToDate(this.selectedWeek)
      return getLastWeekRange(anchor)
    },
    formattedWeekRange() {
      const { start, end } = this.weekRange
      return `${start} 至 ${end}`
    },
    lastWeekLabel() {
      const { start, end } = this.lastWeekRange
      return `${start} ~ ${end}`
    },
    thisWeekLabel() {
      const { start, end } = this.weekRange
      return `${start} ~ ${end}`
    },
    lastWeekChartData() {
      return buildDailyEventChartData(this.lastWeekEventCounts)
    },
    thisWeekChartData() {
      return buildDailyEventChartData(this.thisWeekEventCounts)
    },
    thisWeekEventStatsForExport() {
      return buildEventStatsFromCounts(this.thisWeekEventCounts)
    },
    lastWeekSummaryText() {
      return this.oldEventStr || buildDailyEventSummaryText(this.lastWeekEventCounts)
    },
    thisWeekSummaryText() {
      return this.eventStr || buildDailyEventSummaryText(this.thisWeekEventCounts)
    }
  },
  created() {
    this.startClock()
    this.initPage()
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    formatDate,

    formatDateTime(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}:${s}`
    },

    startClock() {
      this.currentTime = this.formatDateTime(new Date())
      this.timer = setInterval(() => {
        this.currentTime = this.formatDateTime(new Date())
      }, 1000)
    },

    goBack() {
      this.$router.push({ name: 'home' })
    },

    onWeekChange() {
      this.loadProjectData()
    },

    async initPage() {
      await this.loadCommunityList()
      const routeId = this.$route.query.id
      if (routeId) {
        this.selectedProjectId = String(routeId)
      } else if (this.communityList.length) {
        this.selectedProjectId = String(this.communityList[0].id)
      }
      if (this.selectedProjectId) {
        await this.loadProjectData()
      }
    },

    async loadCommunityList() {
      try {
        const cached = localStorage.getItem('communityData')
        if (cached) {
          this.communityList = JSON.parse(cached)
          if (this.communityList.length) return
        }
        const today = new Date()
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const response = await dashboardAPI.getScreenData({
          starttime: formatDate(startOfMonth),
          endtime: formatDate(today)
        })
        const data = response.data || response
        if (data.community_data && Array.isArray(data.community_data)) {
          this.communityList = data.community_data.map((item) => ({
            id: item.id,
            name: item.name || ''
          }))
        }
      } catch (e) {
        console.error('加载项目列表失败', e)
      }
    },

    async loadProjectData() {
      if (!this.selectedProjectId) return
      this.loading = true
      try {
        const { start, end } = this.weekRange
        const response = await dashboardAPI.getScreenReportWeek({
          id: this.selectedProjectId,
          starttime: start,
          endtime: end
        })
        const data = this.getReportData(response)
        this.applyEvents(data)
        this.applyWorkOrders(data)
        this.applySignatures(data)
      } catch (e) {
        console.error('加载周排查数据失败', e)
        this.$message && this.$message.error('加载周排查数据失败')
      } finally {
        this.loading = false
      }
    },

    getReportData(response) {
      if (!response) return {}
      const root = response.data !== undefined ? response.data : response
      if (root && typeof root === 'object' && root.data && typeof root.data === 'object') {
        return root.data
      }
      return root || {}
    },

    normalizeEvents(list) {
      if (!Array.isArray(list)) return []
      return list.map((item) => ({
        createtime: item.createtime || '',
        event_str: item.event_str || item.event_text || '',
        event_name: item.event_name || '',
        event_code: item.event || item.event_code,
        community_name: item.community_name || '',
        picture_file: item.picture_file || item.img || item.image || ''
      }))
    },

    applyEvents(data) {
      this.oldEventStr = data.old_event_str || ''
      this.eventStr = data.event_str || ''

      this.lastWeekEventStats = parseLastWeekEventStats(data)
      this.thisWeekEventStats = parseThisWeekEventStats(data)

      if (Array.isArray(data.last_week_event_stat)) {
        const parsed = parseLastWeekEventStat(data.last_week_event_stat)
        this.lastWeekEventCounts = parsed.counts
        this.lastWeekEvents = parsed.todayEvents
      } else {
        this.applyLegacyWeekEvents(data, 'last')
      }

      const thisStatSource =
        data.today_event_stat ??
        data.this_week_event_stat ??
        data.week_event_stat ??
        data.this_event_stat ??
        null

      if (thisStatSource != null && thisStatSource !== undefined) {
        const parsed = parseTodayEventStat(thisStatSource)
        this.thisWeekEventCounts = parsed.counts
        this.thisWeekEvents = parsed.todayEvents
      } else {
        this.applyLegacyWeekEvents(data, 'this')
      }
    },

    applyLegacyWeekEvents(data, period) {
      const lastRange = this.lastWeekRange
      const thisRange = this.weekRange

      const lastWeekFromApi = this.normalizeEvents(
        data.last_week_event_data || data.lastWeekEventData || []
      )
      const thisWeekFromApi = this.normalizeEvents(
        data.this_week_event_data || data.thisWeekEventData || data.week_event_data || []
      )

      if (period === 'last') {
        if (lastWeekFromApi.length) {
          this.lastWeekEvents = lastWeekFromApi
          this.lastWeekEventCounts = buildEventCountsFromEvents(lastWeekFromApi)
          return
        }

        const all = this.normalizeEvents(data.event_data || [])
        this.lastWeekEvents = all.filter((evt) =>
          isDateInRange(evt.createtime, lastRange.start, lastRange.end)
        )
        this.lastWeekEventCounts = buildEventCountsFromEvents(this.lastWeekEvents)
        return
      }

      if (thisWeekFromApi.length) {
        this.thisWeekEvents = thisWeekFromApi
        this.thisWeekEventCounts = buildEventCountsFromEvents(thisWeekFromApi)
        return
      }

      const all = this.normalizeEvents(data.event_data || [])
      this.thisWeekEvents = all.filter((evt) =>
        isDateInRange(evt.createtime, thisRange.start, thisRange.end)
      )
      this.thisWeekEventCounts = buildEventCountsFromEvents(this.thisWeekEvents)
    },

    parseInspectionOrderList(source) {
      if (!source) return []
      if (Array.isArray(source)) return source.filter((item) => item && typeof item === 'object')
      if (typeof source === 'object') return [source]
      return []
    },

    mapWorkOrderRecord(item) {
      return {
        id: item.id ?? '',
        areaId: item.area_id ?? '',
        areaSiteId: item.area_site_id ?? '',
        code: item.code ?? '',
        createTime: item.createtime ?? item.create_time ?? item.createTime ?? '',
        completeTime: item.completetime ?? '',
        eventId: item.eventid ?? '',
        urgency: item.level_text ?? item.level ?? '',
        orderType: item.order_type_text ?? item.order_type ?? '',
        repairContent: item.repair_content ?? '',
        repairImage: item.repair_images ?? '',
        staffId: item.staff_id ?? '',
        staffImage: item.staff_image ?? '',
        responsibleEmployee: item.repair_staff_id ?? '',
        status: item.status_text ?? item.status ?? '',
        title: item.title ?? '',
        orderImage: item.images ?? '',
        reportEmployee: item.staff_id ?? '',
        orderEvent: item.content ?? '',
        communityAddress: item.area_id ?? ''
      }
    },

    applyWorkOrders(data) {
      const lastList = this.parseInspectionOrderList(data.last_inspection_order)
      const thisList = this.parseInspectionOrderList(data.inspection_order)
      this.lastWeekWorkOrderRecords = lastList.map((item) => this.mapWorkOrderRecord(item))
      this.thisWeekWorkOrderRecords = thisList.map((item) => this.mapWorkOrderRecord(item))
    },

    applySignatures(data) {
      this.staffSignList = this.parseStaffSignList(data.staff_sign)
    },

    parseStaffSignList(staffSign) {
      if (!staffSign) return []

      const fallbackTitles = ['安全总监（手写签名）', '安全负责人（手写签名）']
      const list = Array.isArray(staffSign) ? staffSign : [staffSign]

      return list
        .filter((item) => item && typeof item === 'object')
        .map((item, index) => ({
          title:
            item.title ||
            item.staff_type_text ||
            item.type_text ||
            fallbackTitles[index] ||
            '手写签名',
          staffImage: this.firstImageUrl(item.staff_image || '')
        }))
    },

    firstImageUrl(value) {
      if (!value) return ''
      return String(value).split(',')[0].trim()
    },

    formatEventDisplay(evt) {
      if (evt.event_str) return evt.event_str
      if (evt.event_name) return evt.event_name
      return '-'
    },

    onStaffSignImageError(index) {
      if (this.staffSignList[index]) {
        this.$set(this.staffSignList[index], 'staffImage', '')
      }
    },

    getProjectName() {
      const item = this.communityList.find((c) => String(c.id) === String(this.selectedProjectId))
      return item?.name || '项目'
    },

    async handleExport() {
      this.exporting = true
      try {
        const { start, end } = this.weekRange
        const filename = `海康智慧电梯监测周排查_${this.getProjectName()}_${start}_${end}.xlsx`
        await exportWeeklyInspectionReport({
          formattedWeekRange: this.formattedWeekRange,
          currentTime: this.currentTime,
          showDateInTitle: false,
          reportTitle: '海康智慧电梯监测周排查',
          lastWeekLabel: this.lastWeekLabel,
          thisWeekLabel: this.thisWeekLabel,
          oldEventStr: this.lastWeekSummaryText,
          lastWeekEvents: this.lastWeekEvents,
          eventStr: this.thisWeekSummaryText,
          thisWeekEvents: this.thisWeekEvents,
          thisWeekEventStats: this.thisWeekEventStatsForExport,
          thisPeriodTotalLabel: '本周累计触发事件',
          lastWeekWorkOrderRecords: this.lastWeekWorkOrderRecords,
          thisWeekWorkOrderRecords: this.thisWeekWorkOrderRecords,
          safetyInputs: this.safetyInputs,
          staffSignList: this.staffSignList,
          formatEventDisplay: (evt) => this.formatEventDisplay(evt),
          filename
        })
        this.$message && this.$message.success('导出成功')
      } catch (e) {
        console.error('导出失败', e)
        this.$message && this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped lang="less">
@import './weeklyInspectionStyles.less';
</style>
